import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Tag, 
  Space, 
  Modal, 
  Form,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Row,
  Col,
  Typography,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  SettingOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import adminPlanService from '../../services/adminPlanService';

const { Title } = Typography;
const { TextArea } = Input;

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const data = await adminPlanService.getAllPlans();
      setPlans(data.data || []);
    } catch (error) {
      message.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlan = () => {
    setEditingPlan(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      planId: plan.planId,
      planName: plan.planName,
      description: plan.description,
      price: plan.price,
      currency: plan.currency,
      duration: plan.duration,
      durationInMonths: plan.durationInMonths,
      features: plan.features,
      isActive: plan.isActive
    });
    setModalVisible(true);
  };

  const handleDeletePlan = async (planId) => {
    try {
      await adminPlanService.deletePlan(planId);
      message.success('Plan deleted successfully');
      fetchPlans();
    } catch (error) {
      message.error('Failed to delete plan');
    }
  };

  const handleToggleStatus = async (planId) => {
    try {
      await adminPlanService.togglePlanStatus(planId);
      message.success('Plan status updated successfully');
      fetchPlans();
    } catch (error) {
      message.error('Failed to update plan status');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingPlan) {
        await adminPlanService.updatePlan(editingPlan.planId, values);
        message.success('Plan updated successfully');
      } else {
        await adminPlanService.createPlan(values);
        message.success('Plan created successfully');
      }
      setModalVisible(false);
      fetchPlans();
    } catch (error) {
      message.error(editingPlan ? 'Failed to update plan' : 'Failed to create plan');
    }
  };

  const columns = [
    {
      title: 'Plan ID',
      dataIndex: 'planId',
      key: 'planId',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Plan Name',
      dataIndex: 'planName',
      key: 'planName',
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Price',
      key: 'price',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <span style={{ fontWeight: 'bold' }}>
          {record.currency} {record.price}
        </span>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: 80,
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      align: 'center',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'} style={{ margin: 0 }}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditPlan(record)}
            title="Edit Plan"
          />
          <Button
            size="small"
            icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleStatus(record.planId)}
            title={record.isActive ? 'Deactivate Plan' : 'Activate Plan'}
            style={{ 
              color: record.isActive ? '#ff4d4f' : '#52c41a',
              borderColor: record.isActive ? '#ff4d4f' : '#52c41a'
            }}
          />
          <Popconfirm
            title="Are you sure you want to delete this plan?"
            onConfirm={() => handleDeletePlan(record.planId)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              title="Delete Plan"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>Subscription Plans Management</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPlan}
          >
            Add New Plan
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={plans}
          loading={loading}
          rowKey="planId"
          scroll={{ x: 800 }}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} plans`,
            size: 'small'
          }}
        />
      </Card>

      <Modal
        title={editingPlan ? 'Edit Plan' : 'Add New Plan'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Plan ID"
                name="planId"
                rules={[{ required: true, message: 'Please enter plan ID' }]}
              >
                <Input placeholder="e.g., basic_monthly" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Plan Name"
                name="planName"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="e.g., Basic" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea rows={3} placeholder="Plan description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Price"
                name="price"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="999"
                  min={0}
                  step={1}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: true, message: 'Please enter currency' }]}
              >
                <Input placeholder="INR" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Duration"
                name="duration"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <Input placeholder="1 month" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Duration in Months"
                name="durationInMonths"
                rules={[{ required: true, message: 'Please enter duration in months' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="1"
                  min={1}
                  step={1}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Active Status"
                name="isActive"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Features (JSON Array)"
            name="features"
            rules={[{ required: true, message: 'Please enter features' }]}
          >
            <TextArea
              rows={4}
              placeholder='["Feature 1", "Feature 2", "Feature 3"]'
            />
          </Form.Item>

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPlans;
